import { useAppState } from 'client/providers/AppProvider';
import { Espin_Role_Enum, PlayerProfileUserControlsFragment } from 'generated/spin-graphql';
import React, { useCallback } from 'react';
import { SpinAppModals, useModals } from '../../common/SpinModalProvider';
import { BadgeIcon } from '../../svg/BadgeIcon';
import { MessageIcon } from '../../svg/MessageIcon';
import {
    CheckFollowingStatusForCollegesQuery,
    CheckFollowingStatusForCollegesQueryVariables,
    CheckFollowingStatusForCollegesDocument,
    FollowPlayerMutation,
    FollowPlayerMutationVariables,
    FollowPlayerDocument,
    UnfollowPlayerMutation,
    UnfollowPlayerMutationVariables,
    UnfollowPlayerDocument,
} from 'generated/spin-graphql';
import { useRouter } from 'next/router';
import { useAuthenticatedMutation, useAuthenticatedQuery } from '../../../client/hooks';
import { gql } from 'urql';
import Link from 'next/link';

gql`
    mutation FollowPlayer($collegeId: uuid!, $playerUserId: String!, $collegeName: String!) {
        insert_espin_follow_one(
            object: {
                college_id: $collegeId
                player_user_id: $playerUserId
                college_user_name: $collegeName
                follow_type: college_follows_player
            }
        ) {
            id
        }
    }
`;

gql`
    mutation UnfollowPlayer($playerUserId: String, $collegeId: uuid!) {
        delete_espin_follow(
            where: {
                player_user_id: { _eq: $playerUserId }
                college_id: { _eq: $collegeId }
                follow_type: { _eq: college_follows_player }
            }
        ) {
            returning {
                id
            }
        }
    }

    query CheckFollowingStatusForColleges($playerUserId: String!, $collegeId: uuid!) {
        espin_follow(
            where: {
                player_user_id: { _eq: $playerUserId }
                college_id: { _eq: $collegeId }
                follow_type: { _eq: college_follows_player }
            }
        ) {
            id
        }
    }
`;
gql`
    fragment PlayerProfileUserControls on espin_player_profile {
        user_id
        slug
    }
`;
type Props = {
    player: PlayerProfileUserControlsFragment;
};

export const PlayerProfileUserControls: React.FC<Props> = ({ player }) => {
    const { openModal, closeActiveModal } = useModals();
    const { user, subscribed } = useAppState();

    const router = useRouter();

    // note, this is ALSO in the player's view, so we need to NOT run if we're not a player
    const { data: followingStatus } = useAuthenticatedQuery<
        CheckFollowingStatusForCollegesQuery,
        CheckFollowingStatusForCollegesQueryVariables
    >(
        {
            query: CheckFollowingStatusForCollegesDocument,
            variables: {
                collegeId: user?.college_profile?.college?.id,
                playerUserId: player.user_id,
            },
            pause: !user?.college_profile,
        },
        ['espin_follow'],
    );

    const [_, follow] = useAuthenticatedMutation<FollowPlayerMutation, FollowPlayerMutationVariables>(
        FollowPlayerDocument,
    );

    const [__, unFollow] = useAuthenticatedMutation<UnfollowPlayerMutation, UnfollowPlayerMutationVariables>(
        UnfollowPlayerDocument,
    );

    const handleFollowButton = useCallback(() => {
        if (!user || !subscribed) return router.push('/pricing');
        if (followingStatus?.espin_follow.length <= 0) {
            follow(
                {
                    collegeId: user?.college_profile.college.id,
                    playerUserId: player.user_id,
                    collegeName: `${user?.college_profile?.first_name} ${user?.college_profile?.last_name}`,
                },
                { additionalTypenames: ['espin_follow'] },
            );
        }
        //we unfollow
        else {
            unFollow(
                {
                    collegeId: user?.college_profile.college.id,
                    playerUserId: player.user_id,
                },
                { additionalTypenames: ['espin_follow'] },
            );
        }
    }, [follow, followingStatus?.espin_follow.length, player?.user_id, router, subscribed, unFollow, user]);

    return (
        <div className="user-controls">
            <button
                className="button circle-button"
                title="Give Badge"
                onClick={() => {
                    openModal(SpinAppModals.COMING_SOON, { onCancel: closeActiveModal });
                }}
            >
                <BadgeIcon />
            </button>
            {user?.role === Espin_Role_Enum.EspinCollegeUser && (
                <Link href={subscribed ? `/dashboard/messages/?player_slug=${player.slug}` : `/pricing`}>
                    <a>
                        <button className="button circle-button" title="Send Message">
                            <MessageIcon />
                        </button>
                    </a>
                </Link>
            )}
            {/* we should probably have a more elegant approach here, checking roles? */}
            {router.pathname != '/dashboard/my-profile' ? (
                <button
                    onClick={handleFollowButton}
                    className={`button medium-button${
                        followingStatus?.espin_follow.length > 0 ? ' red-button' : ' blue-button'
                    }`}
                >
                    {subscribed ? (followingStatus?.espin_follow.length <= 0 ? 'Follow' : 'Unfollow') : 'Upgrade'}
                </button>
            ) : null}
        </div>
    );
};
